import React, { PropTypes } from "react";
import R from "ramda";
import moment from "moment";
import openSquirrelUrl from "open-squirrel.svg";
import FaWarning from "react-icons/fa/exclamation-triangle";
import styles from "components/documents/categories-actions-panel.scss";
import grid from "bootstrap/_grid.scss";
import VirtualizedSelect from "react-virtualized-select";
import { documentPropType, categoryType } from "custom-prop-types";
import { splitCategories } from "utils/split-categories";
import { getThisFinancialYear } from "utils/financial-year";
import { isValidValue } from "utils/validators";
import { Button } from "components/common/button";

export const GeneralLedgerActionsPanel = (props) => {
  if (props.isGLCodesVisible && props.filters.type !== "other") {
    return renderGeneralLedgerPanel(props);
  }
  return null;
};

const renderGeneralLedgerPanel = (props) => {
  if (props.glCodes.length === 0) {
    return (
      <div className={ `${styles["categories-actions-container"]} ${styles.none}` }>
        <div className={ styles["no-integration"] }>
          <img alt="open armed squirrel" className={ styles.squirrel } src={ openSquirrelUrl } />
          <p>Darn! It looks like you haven&apos;t set up your accounting integration yet.</p>
          <Button onClick={ props.onLinkAccount } primary>Link your accounting software</Button>
        </div>
      </div>
    );
  }

  return (
    <div className={ styles["categories-actions-container"] }>
      <div className={ styles["categories-title"] }>GL Codes</div>
      {
        props.documents.map((document, index, documentsArray) => (
          renderRules(document, index, documentsArray, props)
        )
        )}
    </div>
  );
};

renderGeneralLedgerPanel.defaultProps = {
  glCodes: []
};

renderGeneralLedgerPanel.propTypes = {
  documents: PropTypes.arrayOf(documentPropType).isRequired,
  glCodes: PropTypes.arrayOf(categoryType),
  onLinkAccount: PropTypes.func.isRequired
};

const renderRules = (document, index, documentsArray, props) => {
  const data = splitCategories(categoriesMapper(document.categories));
  return (
    <div className={ grid.row } key={ document.id }>
      <div className={ `${styles["export-documents"]} ${grid["col-sm-9"]}` }>
        <VirtualizedSelect
          clearable
          onChange={ (value) => { props.setGlCode(document, value); } }
          onInputKeyDown={ (e) => { nextDocument(e, index, documentsArray, props.setDocumentUrl); } }
          options={ optionsMapper(props.glCodes) }
          placeholder={ `Enter a GL code for vendor: ${document.vendor}` }
          resetValue={ { key: "clear-category" } }
          value={ getValue(data) }
        />
      </div>
      <div className={ `${styles["rule-creation"]} ${grid["col-sm-1"]}` }>
        <Button
          onClick={ changeOnClick(
            data,
            document,
            props.onAddIncludeRule,
            props.onAddRuleError
          ) }
          primary
        >
          +
          </Button>
        <div className={ `${styles.tooltip} ${styles["rule-tooltip"]}` }>Add a new rule</div>
      </div>
      { renderError(document, props.preferredCurrency) }
    </div>
  );
};

renderRules.defaultProps = {
  glCodes: []
};

renderRules.propTypes = {
  glCodes: PropTypes.arrayOf(categoryType),
  onAddIncludeRule: PropTypes.func.isRequired,
  onAddRuleError: PropTypes.func.isRequired,
  preferredCurrency: PropTypes.string.isRequired,
  setDocumentUrl: PropTypes.func.isRequired,
  setGlCode: PropTypes.func.isRequired
};

const nextDocument = (e, index, documentsArray, setDocumentUrl) => {
  if (e.key === "Tab") {
    setDocumentUrl(documentsArray[index + 1].attachment.url);
  }
  return null;
};

const categoriesMapper = (categories) => (categories || []).map(category => {
  const strippedCategory = R.drop(1, category.split("-")).join("-");
  return { name: category, key: strippedCategory };
});

const optionsMapper = (value) => value.map(val => ({ label: val.newName, value: val.newName, key: val.name }));

const getValue = (data) => {
  if (data.glCodes[0] === undefined) {
    return null;
  }
  return { label: data.glCodes[0].key, value: data.glCodes[0].name };
};

const changeOnClick = (data, document, onAddIncludeRule, onAddRuleError) => {
  const accountId = document.accountId;
  const vendorName = document.vendor;
  const glCode = data.glCodes.length > 0 ? data.glCodes[0] : [];
  if (data.glCodes.length === 0) {
    return (() => onAddRuleError());
  }
  return (() => onAddIncludeRule({ accountId, glCode, vendorName }));
};

const renderError = (document, preferredCurrency) => {
  const message = getErrorMessage(document, preferredCurrency);
  const errorColor = message.includes(">10% GST") ? styles.red : null;
  if (message !== "") {
    return (
      <div className={ `${grid["col-md-2"]} ${styles.error}` }>
        <FaWarning className={ `${styles.warning} ${errorColor}` } size={ 14 } />
        <div className={ `${styles.tooltip} ${styles["error-tooltip"]}` }>{ message }</div>
      </div>
    );
  }
  return null;
};

const getErrorMessage = (document, preferredCurrency) => {
  let errorMessage = "";
  if (!isValidValue(document.tax) || document.tax === 0) {
    errorMessage = "No GST";
  }
  if (document.tax > (document.total * 0.1)) {
    const text = errorMessage.length > 0 ? ", >10% GST" : ">10% GST";
    errorMessage += text;
  }
  if (moment().isBefore(document.issued)) {
    const text = errorMessage.length > 0 ? ", receipt is future dated" : "Receipt is future dated";
    errorMessage += text;
  }
  const { startDate, endDate } = getThisFinancialYear(preferredCurrency);
  if (!moment(document.issued).isBetween(startDate, endDate)) {
    const text = errorMessage.length > 0 ?
      ", document is from last financial year" : "Document is from last financial year";
    errorMessage += text;
  }
  return errorMessage;
};



// WEBPACK FOOTER //
// ./src/js/components/documents/exports/general-ledger-actions-panel.jsx